import { useQuery } from '@tanstack/react-query';
import { t } from '../lib/i18n';
import { Badge, PageHeader, Panel } from '../ui/Page';
import { DataTable, type Column } from '../ui/DataTable';
import { listQueues, type QueueView } from './api';

/** Queues and the SLA each one promises (context.md sections 48-52). */
export function QueuesPage(): React.JSX.Element {
  const queues = useQuery({ queryKey: ['queues'], queryFn: listQueues });

  const columns: Column<QueueView>[] = [
    {
      key: 'name',
      header: t('queues.name'),
      render: (queue) => (
        <span className="cell-stack">
          <span className="strong">{queue.name}</span>
          <span className="muted small">{queue.description ?? `/${queue.slug}`}</span>
        </span>
      ),
    },
    {
      key: 'strategy',
      header: t('queues.strategy'),
      render: (queue) => <Badge tone="info">{t(`queues.strategy.${queue.strategy}`)}</Badge>,
    },
    {
      key: 'first',
      header: t('queues.firstResponse'),
      render: (queue) => minutes(queue.first_response_minutes),
    },
    {
      key: 'resolution',
      header: t('queues.resolution'),
      secondary: true,
      render: (queue) => minutes(queue.resolution_minutes),
    },
    {
      key: 'hours',
      header: t('queues.businessHours'),
      secondary: true,
      render: (queue) => (queue.business_hours_only ? t('common.yes') : t('common.no')),
    },
    {
      key: 'default',
      header: t('queues.default'),
      align: 'end',
      render: (queue) =>
        queue.is_default ? <Badge tone="positive">{t('common.yes')}</Badge> : '—',
    },
  ];

  return (
    <div className="view">
      <PageHeader title={t('nav.queues')} description={t('queues.description')} />

      <Panel>
        <DataTable
          tableKey="queues"
          rows={queues.data?.queues}
          columns={columns}
          rowKey={(queue) => queue.id}
          isLoading={queues.isLoading}
          error={queues.error}
          onRetry={() => void queues.refetch()}
          empty={{ title: t('queues.emptyTitle'), description: t('queues.emptyBody') }}
        />
      </Panel>
    </div>
  );
}

/** A queue that promises nothing about a leg says so, rather than showing a zero. */
function minutes(value: number | null): string {
  if (value === null) return t('queues.noPromise');

  if (value < 60) return t('queues.minutes').replace('{n}', String(value));

  const hours = Math.round((value / 60) * 10) / 10;

  return t('queues.hours').replace('{n}', String(hours));
}
